/**
 * usePmNotifications — fetches the PM notification log with filters + pagination.
 *
 * Admin-only endpoint: the backend returns 403 for non-admins. This hook
 * propagates the error string to the caller; the page is responsible for
 * rendering an appropriate access-denied message.
 *
 * The API returns results ordered failures-first (backend ordering). This
 * hook makes no further client-side sort — it trusts the server ordering.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, ListPmNotificationsParams, pmNotifications } from '@/helpers/ApiHelper';
import type { PaginatedPmNotifications } from '@/types/pm_notification';

interface UsePmNotificationsResult {
  data: PaginatedPmNotifications | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePmNotifications(
  params: ListPmNotificationsParams = {},
): UsePmNotificationsResult {
  const [data, setData] = useState<PaginatedPmNotifications | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  // Destructure params to keep the dep array stable across re-renders without
  // requiring the caller to memoize the params object.
  const { status, notification_type, notion_id, page } = params;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        const result = await pmNotifications.list({
          status,
          notification_type,
          notion_id,
          page,
        });
        if (!cancelled) {
          setData(result);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.detail : 'Failed to load notifications');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, notification_type, notion_id, page, tick]);

  return { data, isLoading, error, refetch };
}
